import { AnimatePresence, motion } from "framer-motion";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import ProtectedRoute from "./components/ProtectedRoute";

import ForgotPassword from "./components/ForgotPassword";
import Login from "./components/Login";
import ResetPassword from "./components/ResetPassword";
import Home from "./pages/Home";
import PhoneOtpAuth from "./pages/PhoneOtpAuth";
import Register from "./pages/Register";
import RegisterDetails from "./pages/RegisterDetails";

import EditIssuePage from "./pages/EditIssuePage";
import IssueDetail from "./pages/IssueDetail";
import IssuesPage from "./pages/IssuesPage";
import MyIssuesPage from "./pages/MyIssuesPage";
import RaiseIssue from "./pages/RaiseIssue";

import BadgesPage from "./pages/BadgesPage";
import DiscussionForum from "./pages/DiscussionForum";
import EditProfilePage from "./pages/EditProfilepage";
import ExploreCandidates from "./pages/ExploreCandidates";
import MyConstituencyPage from "./pages/MyConstituencyPage";
import VoterDashboard from "./pages/VoterDashboard";

import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import AdminVerifyClaims from "./pages/AdminVerifyClaims";
import AdminLayout from "./components/admin/AdminLayout";
import AnalyticsPanel from "./components/admin/AnalyticsPanel";
import ClaimsManagementPanel from "./components/admin/ClaimsManagementPanel";
import FlaggedIssuesPanel from "./components/admin/FlaggedIssuesPanel";
import IssuesManagementPanel from "./components/admin/IssuesManagementPanel";
import SettingsPanel from "./components/admin/SettingsPanel";
import UserManagementPanel from "./components/admin/UserManagementPanel";
import CandidateDashboard from "./pages/CandidateDashboard";
import CandidateProfile from "./pages/CandidateProfile";
import ClaimCandidateProfile from "./pages/ClaimCandidateProfile";

import ConstituencyProfilePage from "./pages/ConstituencyProfilePage";
import StatePage from "./pages/StatePage";

import TestPage from "./components/TestPage";
import DonatePage from "./pages/DonatePage";
import ElectionPortal from "./pages/ElectionPortal";
import JanSabhaFeed from "./pages/JanSabhaFeed";
import LiveProtestPage from "./pages/LiveProtestPage";
import MemeIssuePage from "./pages/MemeIssuePage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsPage from "./pages/TermsPage";

import DevelopmentNoticeModal from "./components/DevelopmentNoticeModal";
import { AuthProvider } from "./context/AuthContext";

/* ---------------- Animations ---------------- */

const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -18 }}
    transition={{ duration: 0.28, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

/* ---------------- Layout ---------------- */

function LayoutWrapper() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const shouldShowPublicChrome = !isAdminRoute;

  const authRoutes = [
    "/login",
    "/register",
    "/verify-phone",
    "/phone-auth",
    "/register-details",
  ];

  const shouldShowFooter =
    shouldShowPublicChrome && !authRoutes.includes(location.pathname);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ✅ ADD MODAL HERE (CORRECT PLACE) */}
      <DevelopmentNoticeModal />

      {shouldShowPublicChrome && <Navbar />}

      <div
        className={`min-h-screen flex flex-col bg-slate-50 ${
          shouldShowPublicChrome ? "pt-24" : ""
        }`}
      >
        <div className="flex-grow">
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              
              <Route path="/" element={<PageTransition><Home /></PageTransition>} />
              <Route path="/home" element={<PageTransition><Home /></PageTransition>} />

              <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
              <Route path="/register" element={<PageTransition><Register /></PageTransition>} />
              <Route path="/verify-phone" element={<PageTransition><PhoneOtpAuth /></PageTransition>} />
              <Route path="/phone-auth" element={<PageTransition><PhoneOtpAuth /></PageTransition>} />
              <Route path="/register-details" element={<PageTransition><RegisterDetails /></PageTransition>} />
              <Route path="/forgot-password" element={<PageTransition><ForgotPassword /></PageTransition>} />
              <Route path="/reset-password/:token" element={<PageTransition><ResetPassword /></PageTransition>} />

              <Route
                path="/raise-issue"
                element={
                  <ProtectedRoute>
                    <PageTransition><RaiseIssue /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route path="/issues" element={<PageTransition><IssuesPage /></PageTransition>} />
              <Route path="/issues/:id" element={<PageTransition><IssueDetail /></PageTransition>} />
              <Route
                path="/edit-issue/:id"
                element={
                  <ProtectedRoute>
                    <PageTransition><EditIssuePage /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-issues"
                element={
                  <ProtectedRoute>
                    <PageTransition><MyIssuesPage /></PageTransition>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute roles={["voter"]}>
                    <PageTransition><VoterDashboard /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/voter-dashboard"
                element={
                  <ProtectedRoute roles={["voter"]}>
                    <PageTransition><VoterDashboard /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-constituency"
                element={
                  <ProtectedRoute roles={["voter"]}>
                    <PageTransition><MyConstituencyPage /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route path="/discussions" element={<PageTransition><DiscussionForum /></PageTransition>} />
              <Route path="/badges" element={<PageTransition><BadgesPage /></PageTransition>} />
              <Route
                path="/edit-profile"
                element={
                  <ProtectedRoute>
                    <PageTransition><EditProfilePage /></PageTransition>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/candidate-dashboard"
                element={
                  <ProtectedRoute roles={["candidate"]}>
                    <PageTransition><CandidateDashboard /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/candidate/dashboard"
                element={
                  <ProtectedRoute roles={["candidate"]}>
                    <PageTransition><CandidateDashboard /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/candidate/claim"
                element={
                  <ProtectedRoute>
                    <PageTransition><ClaimCandidateProfile /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/claim-candidate"
                element={
                  <ProtectedRoute>
                    <PageTransition><ClaimCandidateProfile /></PageTransition>
                  </ProtectedRoute>
                }
              />
              <Route path="/candidate/:name" element={<PageTransition><CandidateProfile /></PageTransition>} />
              <Route path="/explore-candidates" element={<PageTransition><ExploreCandidates /></PageTransition>} />

              <Route path="/state/:stateName" element={<PageTransition><StatePage /></PageTransition>} />

              <Route
                path="/constituency/:type/:constituencyName"
                element={<PageTransition><ConstituencyProfilePage /></PageTransition>}
              />

              <Route path="/jan-sabha" element={<PageTransition><JanSabhaFeed /></PageTransition>} />
              <Route path="/protests" element={<PageTransition><LiveProtestPage /></PageTransition>} />
              <Route path="/meme-issues" element={<PageTransition><MemeIssuePage /></PageTransition>} />
              <Route path="/elections/register" element={<PageTransition><ElectionPortal /></PageTransition>} />

              <Route path="/admin-login" element={<PageTransition><AdminLogin /></PageTransition>} />
              <Route
                path="/admin/verify-claims"
                element={
                  <ProtectedRoute roles={["admin"]} redirectTo="/admin-login">
                    <PageTransition>
                      <AdminVerifyClaims />
                    </PageTransition>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin-dashboard"
                element={
                  <ProtectedRoute roles={["admin"]} redirectTo="/admin-login">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="flagged" element={<FlaggedIssuesPanel />} />
                <Route path="users" element={<UserManagementPanel />} />
                <Route path="issues" element={<IssuesManagementPanel />} />
                <Route path="claims" element={<ClaimsManagementPanel />} />
                <Route path="analytics" element={<AnalyticsPanel />} />
                <Route path="settings" element={<SettingsPanel />} />
              </Route>

              <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
              <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
              <Route path="/donate" element={<PageTransition><DonatePage /></PageTransition>} />
              <Route path="/test-card" element={<PageTransition><TestPage /></PageTransition>} />

            </Routes>
          </AnimatePresence>
        </div>

        {shouldShowFooter && <Footer />}
      </div>
    </>
  );
}

/* ---------------- APP ---------------- */

function App() {
  return (
    <Router>
      <AuthProvider>
        <LayoutWrapper />
      </AuthProvider>
    </Router>
  );
}

export default App;
